import React, { useLayoutEffect, useRef, useState } from "react";
import { useSettings } from "@/lib/SettingsContext";
import "@/styles/redesign.css";

const DEFAULT_TEXT = "DUBAI · INDIA · MIDDLE EAST · TECHNICAL PRODUCTION · 500+ EVENTS DELIVERED · IDEAS · INNOVATION · TECHNOLOGY · AUDIO · LIGHTING · LED · PROJECTION · RIGGING · SFX";

const splitWithSeparators = (text: string): React.ReactNode[] => {
  const parts = text.split(/(\u00b7)/);
  return parts.map((p, i) => p === "·"
    ? <span key={i} className="iav-anno-sep">{p}</span>
    : <React.Fragment key={i}>{p}</React.Fragment>);
};

// Fallback speeds (pixels per second) for the legacy slow/medium/fast setting,
// used only when the admin slider value is absent.
const SPEED_PX_PER_SEC: Record<string, number> = { slow: 45, medium: 75, fast: 130 };

// Must match the `gap` and `padding-left` on .iav-anno-track in redesign.css.
const GAP_PX = 56;

const AnnouncementBar: React.FC = () => {
  const { settings } = useSettings();
  const wrapRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const [copies, setCopies] = useState(2);
  const [duration, setDuration] = useState("28s");

  const hidden = settings.announcement_visible === "false" || settings.announcement_bar_enabled === "false";
  const block = (settings.announcement_text || settings.announcement_bar_text || DEFAULT_TEXT).trim();

  // The track animates to translateX(-50%), which only loops seamlessly when the
  // track holds an EVEN number of copies AND its first half is at least as wide
  // as the bar itself. Short messages therefore need more copies than long ones,
  // so measure the rendered text and repeat it as many times as required.
  useLayoutEffect(() => {
    if (hidden || !block) return;
    let cancelled = false;

    const compute = () => {
      if (cancelled) return;
      const wrap = wrapRef.current;
      const item = measureRef.current;
      if (!wrap || !item) return;

      const itemW = item.getBoundingClientRect().width + GAP_PX;
      const containerW = wrap.getBoundingClientRect().width;
      if (itemW <= GAP_PX || containerW <= 0) return;

      // +1 gives a full item of headroom so no gap can appear at the loop point.
      const perHalf = Math.ceil(containerW / itemW) + 1;
      setCopies(perHalf * 2);

      const halfW = perHalf * itemW;

      // Speed is always resolved to pixels-per-second, then converted into a
      // duration for the ACTUAL track width. A fixed loop duration must never be
      // used directly: the track grows with the message, so the same duration
      // would make a long message scroll several times faster than a short one.
      let pxPerSec: number;
      const sliderMs = settings.announcement_speed_ms ? parseInt(settings.announcement_speed_ms, 10) : 0;
      if (sliderMs > 0) {
        // The admin slider stores "seconds for the text to travel one bar width".
        pxPerSec = containerW / (sliderMs / 1000);
      } else {
        const key = settings.announcement_speed === "slow" ? "slow"
          : settings.announcement_speed === "fast" ? "fast"
          : "medium";
        pxPerSec = SPEED_PX_PER_SEC[key];
      }
      if (!isFinite(pxPerSec) || pxPerSec <= 0) pxPerSec = SPEED_PX_PER_SEC.medium;

      setDuration(`${Math.max(4, Math.round(halfW / pxPerSec))}s`);
    };

    compute();

    // Re-measure once webfonts settle. Measuring against a fallback font gives a
    // wrong width, which shifts the loop point. document.fonts.ready resolves
    // deterministically; the timeout is only a fallback for browsers without the
    // Font Loading API.
    let timer = 0;
    const fonts = (document as any).fonts;
    if (fonts && typeof fonts.ready?.then === "function") {
      fonts.ready.then(compute).catch(() => { /* ignore */ });
    } else {
      timer = window.setTimeout(compute, 400);
    }

    window.addEventListener("resize", compute);
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
      window.removeEventListener("resize", compute);
    };
  }, [hidden, block, settings.announcement_speed, settings.announcement_speed_ms]);

  if (hidden || !block) return null;

  const content = splitWithSeparators(block.toUpperCase());

  return (
    <div className="iav-anno" ref={wrapRef} role="complementary" aria-label="Announcements" data-testid="announcement-bar">
      <div className="iav-anno-track" style={{ animationDuration: duration }}>
        {Array.from({ length: copies }).map((_, i) => (
          <span className="iav-anno-item" key={i} aria-hidden={i > 0}>{content}</span>
        ))}
      </div>
      {/* Off-screen copy used purely to measure one repetition. Never visible. */}
      <span className="iav-anno-item iav-anno-measure" ref={measureRef} aria-hidden>{content}</span>
    </div>
  );
};

export default AnnouncementBar;
